import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { CAI } from "@/constants/testIds";

const fmt = (c) => `$${(c / 100).toFixed(2)} CAD`;

export default function CaiDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/economics/dashboard").then(({ data: d }) => {
      if (!cancelled) setData(d);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data)
    return (
      <div className="min-h-screen bg-[#0b0c10]">
        <Navbar />
        <div className="p-8 text-slate-400 font-mono text-sm">loading dashboard…</div>
      </div>
    );

  const cards = [
    { id: CAI.boileauCard, label: "Boileau · 25% net revenue", val: data.boileau_share_cents, accent: "text-yellow-400", desc: "25% of every membership routed to the founder." },
    { id: CAI.painCard, label: "Pain & Suffering · 15%", val: data.pain_pool_cents, accent: "text-rose-400", desc: `Claimed: ${fmt(data.pain_pool_claimed_cents)} · Remaining: ${fmt(data.pain_pool_remaining_cents)}` },
    { id: CAI.altCard, label: "Redistribution Pool · 60%", val: data.alt_financier_pool_cents, accent: "text-emerald-400", desc: `Available: ${fmt(data.alt_pool_available_cents)} · 2% cap/req: ${fmt(data.alt_per_request_cap_cents)}` },
  ];

  const splits = [
    { label: "Pain & Suffering", pct: 15, color: "bg-rose-500" },
    { label: "Boileau", pct: 25, color: "bg-yellow-400" },
    { label: "Redistribution Pool", pct: 60, color: "bg-emerald-500" },
  ];

  return (
    <div className="min-h-screen bg-[#0b0c10] telesis-grain" data-testid={CAI.container}>
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <p className="telesis-eyebrow">CAI Ledger · {data.month}</p>
        <h1 className="text-3xl md:text-4xl font-black mt-2 text-slate-100 tracking-tight">
          The redistribution of {fmt(data.gross_revenue_cents)} gross this month
        </h1>
        <p className="mt-3 text-slate-400 text-sm max-w-2xl">
          {data.paid_members} paid member{data.paid_members === 1 ? "" : "s"} × {fmt(data.membership_cents)} membership. All math is public.
        </p>

        <section className="mt-8 grid md:grid-cols-3 gap-4">
          {cards.map((c) => (
            <div key={c.id} data-testid={c.id} className="telesis-panel rounded p-6 telesis-rise">
              <p className="telesis-eyebrow">{c.label}</p>
              <p className={`text-3xl md:text-4xl font-black mt-3 ${c.accent}`}>{fmt(c.val)}</p>
              <p className="text-xs text-slate-400 mt-3 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </section>

        <section className="mt-8 telesis-panel rounded p-6" data-testid={CAI.waterfall}>
          <p className="telesis-eyebrow mb-3">Monthly waterfall</p>
          <div className="h-5 w-full flex overflow-hidden rounded">
            {splits.map((s) => (
              <div
                key={s.label}
                className={s.color}
                style={{ width: `${s.pct}%` }}
                title={`${s.label} — ${s.pct}%`}
              />
            ))}
          </div>
          <div className="mt-3 grid md:grid-cols-3 gap-3 text-xs font-mono text-slate-300">
            {splits.map((s) => (
              <div key={s.label} className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-sm ${s.color}`} />
                <span>{s.label} · <b>{s.pct}%</b></span>
              </div>
            ))}
          </div>
        </section>

        <section data-testid={CAI.rolloverIndicator} className="mt-8 telesis-panel rounded p-6 border-l-2 border-rose-500/40">
          <p className="telesis-eyebrow">Rollover indicator</p>
          <p className="text-sm text-slate-200 mt-2">
            Unclaimed Pain &amp; Suffering remaining this month:{" "}
            <b className="text-rose-300">{fmt(data.pain_pool_remaining_cents)}</b>
          </p>
          <p className="text-sm text-slate-400 mt-2">
            Projected rollover to Alternate Financier Pool at month end:{" "}
            <b className="text-emerald-300">{fmt(data.projected_rollover_to_alt_cents)}</b>
          </p>
        </section>
      </main>
    </div>
  );
}
